import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoCart, IoMenu, IoClose } from 'react-icons/io5';
import { useShop } from '../context/ShopContext';

const Navbar = ({ onOpenTrack }) => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cart, region, toggleRegion, setIsCartOpen } = useShop();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Products', href: '/#products' },
    { name: 'Science', href: '/#science' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 'var(--nav-height)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 5%',
          zIndex: 1000,
          background: scrolled || isMobileMenuOpen ? 'rgba(0, 0, 0, 0.94)' : 'transparent',
          backdropFilter: scrolled || isMobileMenuOpen ? 'blur(20px)' : 'none',
          transition: 'background 0.3s ease',
          borderBottom: scrolled || isMobileMenuOpen ? '1px solid #222222' : '1px solid transparent',
        }}
      >
        {/* Mobile Hamburger Icon was moved to the right */}

        {/* Logo */}
        <a href="#" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <img
            src="/LOGO.jpeg"
            alt="MITOFORGE"
            style={{
              height: '32px',
              width: 'auto',
              objectFit: 'contain'
            }}
            className="nav-logo-img"
          />
          <span style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1 }} className="nav-logo-text-container">
            <span style={{
              color: '#ffffff',
              fontSize: '1.2rem',
              fontWeight: 800,
              letterSpacing: '0.12em'
            }} className="nav-logo-text">
              MITOFORGE<sup style={{ fontSize: '0.45em', fontWeight: 600, verticalAlign: 'super', letterSpacing: 0 }}>®</sup>
            </span>
            <span style={{
              color: '#666666',
              fontSize: '0.5rem',
              fontWeight: 600,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              marginTop: '4px'
            }} className="nav-logo-sub">
              Cordyceps Militaris
            </span>
          </span>
        </a>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }} className="nav-right-container">
          {/* Desktop Links */}
          <div className="desktop-links" style={{ display: 'flex', gap: '2rem' }}>
            {navLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                style={{
                  color: '#888888',
                  textDecoration: 'none',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.color = '#ffffff'}
                onMouseLeave={(e) => e.target.style.color = '#888888'}
              >
                {item.name}
              </a>
            ))}
            <button
              onClick={onOpenTrack}
              style={{
                color: '#ffffff',
                textDecoration: 'none',
                fontSize: '0.88rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.7'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              Track Order
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', borderLeft: '1px solid #222222', paddingLeft: '1.5rem' }} className="nav-actions-group">
            <button
              onClick={toggleRegion}
              style={{
                color: '#ffffff',
                fontSize: '0.82rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #333333',
                background: 'transparent',
                padding: '6px 10px',
                borderRadius: '4px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#ffffff'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#333333'}
            >
              <span>{region === 'IN' ? 'INR ₹' : 'USD $'}</span>
            </button>

            <button
              onClick={() => setIsCartOpen(true)}
              style={{
                position: 'relative',
                color: '#000000',
                background: '#ffffff',
                padding: '8px 14px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <IoCart size={18} color="#000000" />
              <span className="cart-text">Cart</span>
              {cart.length > 0 && (
                <span style={{
                  background: '#000000',
                  color: '#ffffff',
                  fontSize: '10px',
                  fontWeight: 800,
                  padding: '2px 6px',
                  borderRadius: '2px'
                }}>
                  {cart.reduce((a, b) => a + (b.quantity || 1), 0)}
                </span>
              )}
            </button>

            {/* Mobile Hamburger Icon */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                display: 'none',
                color: '#ffffff',
                padding: '4px',
                marginLeft: '4px'
              }}
              className="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <IoClose size={28} /> : <IoMenu size={28} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              top: 'var(--nav-height)',
              left: 0,
              right: 0,
              bottom: 0,
              background: '#000000',
              zIndex: 999,
              padding: '2rem 5%',
              display: 'flex',
              flexDirection: 'column',
              gap: '2rem',
              borderTop: '1px solid #111111'
            }}
          >
            {navLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  color: '#ffffff',
                  textDecoration: 'none',
                  fontSize: '1.5rem',
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                {item.name}
              </a>
            ))}
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenTrack();
              }}
              style={{
                color: '#ffffff',
                textAlign: 'left',
                fontSize: '1.5rem',
                fontWeight: 800,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              Track Order
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 900px) {
          .desktop-links {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
          .nav-right-container {
            gap: 0 !important;
          }
          .nav-actions-group {
            border-left: none !important;
            padding-left: 0 !important;
          }
        }
        @media (max-width: 500px) {
          .nav-logo-sub {
            display: none !important;
          }
          .nav-logo-text-container {
            display: none !important;
          }
          .nav-logo-img {
            height: 28px !important;
          }
          .cart-text {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;
